// No React import needed with automatic JSX runtime

type CryptoTickerCloudProps = {
  symbols?: string[];
  className?: string;
  opacity?: number; // 0..1
};

// Floating crypto tickers to suggest live market motion without visual overload.
export default function CryptoTickerCloud({
  symbols = [
    "BTC",
    "ETH",
    "SOL",
    "ADA",
    "XRP",
    "AVAX",
    "DOGE",
    "MATIC",
    "DOT",
    "ARB",
  ],
  className = "",
  opacity = 0.85,
}: CryptoTickerCloudProps) {
  // Deterministic scatter positions (top/left in %), tuned for hero area
  const positions: Array<{ top: number; left: number; scale: number; delay: number; speed: number }> = [
    { top: 12, left: 18, scale: 1.0, delay: 0, speed: 8 },
    { top: 28, left: 6, scale: 0.9, delay: 0.6, speed: 10 },
    { top: 44, left: 22, scale: 1.1, delay: 1.2, speed: 9 },
    { top: 60, left: 10, scale: 0.95, delay: 0.3, speed: 11 },
    { top: 18, left: 60, scale: 1.0, delay: 0.9, speed: 8 },
    { top: 36, left: 72, scale: 0.9, delay: 1.5, speed: 12 },
    { top: 58, left: 82, scale: 1.05, delay: 0.4, speed: 9 },
    { top: 74, left: 66, scale: 0.92, delay: 1.8, speed: 10 },
    { top: 8, left: 80, scale: 0.88, delay: 1.1, speed: 11 },
    { top: 70, left: 30, scale: 1.0, delay: 0.7, speed: 9 },
  ];

  return (
    <div className={`pointer-events-none absolute inset-0 ${className}`} style={{ opacity }}>
      <style>{`
        @keyframes driftY { 0% { transform: translateY(0px); } 50% { transform: translateY(-8px); } 100% { transform: translateY(0px); } }
        @keyframes floatX { 0% { transform: translateX(0px); } 50% { transform: translateX(14px); } 100% { transform: translateX(0px); } }
      `}</style>
      {symbols.slice(0, positions.length).map((sym, i) => {
        const p = positions[i];
        return (
          <div
            key={sym + i}
            className="absolute"
            style={{ top: `${p.top}%`, left: `${p.left}%`, transform: `scale(${p.scale})` }}
          >
            <div
              className="animate-[driftY_var(--speed)_ease-in-out_infinite]"
              style={{
                // Convert seconds to CSS variable used in animation shorthand
                // Tailwind can't tokenize custom keyframes with dynamic durations inline, so use style var.
                ['--speed' as any]: `${p.speed}s`,
              }}
            >
              <div
                className="animate-[floatX_var(--speed2)_ease-in-out_infinite]"
                style={{ ['--speed2' as any]: `${p.speed + 2}s`, animationDelay: `${p.delay}s` }}
              >
                <span
                  className="px-3 py-1 rounded-full border text-xs font-mono tracking-wide"
                  style={{
                    borderColor: "hsl(193,100%,50%)",
                    color: "hsl(210,9%,91%)",
                    background: "hsla(193,100%,50%,0.10)",
                    boxShadow: "0 0 24px hsla(193,100%,50%,0.20)",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  {sym}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
